import React from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components/macro";
import { COLORS, FONTSIZE, FONTWEIGHT } from "helpers/designConstants";

/**
 * Lives in the picker's non-scrolling SectionHeader row, so it stays visible while results scroll.
 * Replaces the hardcoded "Performing Search..." which never cleared — on a zero-result search it
 * left a blank panel claiming it was still working.
 *
 * Strings use t(key, fallback): this app loads translations from hosted i18nexus, so keys cannot be
 * added in code. The fallback renders correct English until the operator adds them.
 */
const SearchStatusLine = ({ status }) => {
  const { t } = useTranslation();
  if (status == null) return null;

  const { kind, count, term } = status;

  if (kind === "searching")
    return <Line>{t("search_status_searching", "Searching…")}</Line>;

  if (kind === "preview")
    return (
      <Line>
        {t(
          "search_status_preview",
          "Preview — showing current-season matches. Type 3 characters for full results."
        )}
      </Line>
    );

  if (kind === "truncated")
    return (
      <Line isWarning>
        {t(
          "search_status_truncated",
          "Showing the first 250 matches — narrow your search."
        )}
      </Line>
    );

  if (kind === "error")
    return (
      <Line isWarning>
        {t("search_status_error", "Search unavailable — try again.")}
      </Line>
    );

  // escapeValue:false because React already escapes everything it renders. Left on (the i18next
  // default, and services/i18n.js sets no interpolation config) it double-escapes: a season named
  // "Spring/Summer 26" reached the rep as "Spring&#x2F;Summer 26".
  if (kind === "empty")
    return (
      <Line>
        {t(
          "search_status_empty",
          'No products match "{{term}}". Only the current seasons are searched.',
          { term, interpolation: { escapeValue: false } }
        )}
      </Line>
    );

  // {{total}}, not {{count}} — i18next treats `count` as a plural trigger and would resolve to
  // search_status_count_one / _other, which do not exist.
  return (
    <Line>{t("search_status_count", "{{total}} products", { total: count })}</Line>
  );
};

const Line = styled.div`
  padding-left: 10px;
  font-size: ${FONTSIZE.small};
  font-weight: ${FONTWEIGHT.medium};
  color: ${({ isWarning }) => (isWarning ? COLORS.alertOrange : COLORS.text)};
`;

export default SearchStatusLine;
